'use client';

import { HomePage, HomePageItem, HomePageItemType } from '@/models/HomePage';

const SeoView = dynamic(() => import('./seo/SeoView').then((mod) => mod.SeoView), { ssr: false });
const DynamicOrderFeedbackFlow = dynamic(() => import('@/components/OrderFeedbackFlow'), {
  ssr: false,
});

import { fetchProductPrices } from '@/lib/productPrices';
import { WebLink } from '@/lib/WebLinks';
import { PriceData } from '@/models/Product';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useEffect, useMemo, useState } from 'react';

import { getAppVersionCode, getOsType } from '@/lib/CookieUtils';
import { isAppClient } from '@/lib/LocalStorage';
import { MenuCategory } from '@/models/MenuCategories';
import { BannerUnderContentStrip } from './BannerUnderContentStrip';
import { CategoryIconStrip } from './CategoryIconStrip';
import ClawDailyPopup from './ClawDailyPopup';
import HomepageItem from './HomepageItem';
import LazySection from './LazySection';

const DynamicHomepageFavorites = dynamic(() => import('./HomepageFavorites'), {
  ssr: false,
});

const FALLBACK_BANNER_UNDER_ITEMS: HomePageItem[] = [
  {
    type: HomePageItemType.BANNER_UNDER_CONTENT,
    showCategoryTitle: 0,
    title: 'Hızlı Çiçek Ustalarının Elinden',
    description:
      'Siparişleriniz, her biri konusunda uzman, Hızlı Çiçek ustalarımız tarafından hazırlanır.',
    imageUrl: '/home_badges/hizli-cicek-ustalari.png',
  },
  {
    type: HomePageItemType.BANNER_UNDER_CONTENT,
    showCategoryTitle: 0,
    title: 'Doğrudan Kendi Depolarımızdan',
    description: 'Tüm siparişler, aracı olmadan doğrudan kendi depolarımızda hazırlanır.',
    imageUrl: '/home_badges/kendi-depolarimiz.png',
  },
  {
    type: HomePageItemType.BANNER_UNDER_CONTENT,
    showCategoryTitle: 0,
    title: '30 Dakikada Teslimat',
    description: 'İstanbul, Ankara, İzmir ve Bursa’nın birçok noktasına 30 dakikada teslimat.',
    imageUrl: '/home_badges/30-dakikada-teslim.png',
    url: '/30-dakikada-cicek-gonder',
  },
];

export default function HomePageContent({
  className,
  homePageResult,
  categoryStripCategories,
}: {
  className?: string;
  homePageResult: HomePage;
  /** Slim strip data — not the full menu tree (payload-friendly). */
  categoryStripCategories?: MenuCategory[];
}) {
  const [pricesData, setPricesData] = useState<PriceData | null>(null);
  const [pageIsApp, setPageIsApp] = useState(false);
  const [feedbackFlowDebug, setFeedbackFlowDebug] = useState(false);
  const showFeedbackFlow = useMemo(() => {
    if (feedbackFlowDebug) return true;
    if (!pageIsApp) return false;

    const osType = getOsType();
    const versionStr = getAppVersionCode();
    const versionCode = versionStr ? Number(versionStr) : NaN;

    if (!osType || Number.isNaN(versionCode)) return false;

    if (osType === 'android') return versionCode >= 10104;
    if (osType === 'ios') return versionCode >= 10104;

    return false;
  }, [pageIsApp, feedbackFlowDebug]);

  useEffect(() => {
    setPageIsApp(isAppClient());
    try {
      setFeedbackFlowDebug(new URLSearchParams(window.location.search).get('feedbackFlow') === '1');
    } catch (_) {
      setFeedbackFlowDebug(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchProductPrices().then((data) => {
      if (!cancelled) setPricesData(data);
    });
    return () => {
      cancelled = true;
    };
  }, [homePageResult]);

  const allItems = homePageResult?.homepage?.items ?? [];
  const cmsBannerUnderItems = allItems.filter(
    (item) => item.type === HomePageItemType.BANNER_UNDER_CONTENT,
  );
  const bannerUnderItems =
    cmsBannerUnderItems.length > 0 ? cmsBannerUnderItems : FALLBACK_BANNER_UNDER_ITEMS;
  const contentItems = allItems.filter(
    (item) => item.type !== HomePageItemType.BANNER_UNDER_CONTENT,
  );

  const items = contentItems.map((item, index) => {
    const isFirstTwo = index < 2;
    const nodeItem = (
      <HomepageItem
        key={isFirstTwo ? index : null}
        item={item}
        index={index}
        isFirstItem={index === 0}
        pricesData={pricesData}
      />
    );
    return isFirstTwo ? nodeItem : <LazySection key={index}>{nodeItem}</LazySection>;
  });
  const cicekChildren = categoryStripCategories ?? [];

  return (
    <div className={`${className} homepage`}>
      {cicekChildren.length > 0 && (
        <div className="max-w-7xl w-full mx-auto px-4 md:px-0 mb-2">
          <CategoryIconStrip categories={cicekChildren} />
        </div>
      )}
      {pageIsApp && <ClawDailyPopup />}
      {showFeedbackFlow && <DynamicOrderFeedbackFlow />}
      <h1 className="sr-only">Hızlı Çiçek - Online Çiçek Siparişi</h1>
      <div
        className="
        flex
        overflow-x-auto overscroll-x-contain
        snap-x snap-mandatory
        [scrollbar-width:none] [-ms-overflow-style:none]
        [&::-webkit-scrollbar]:hidden
        gap-2
        px-2
        justify-between
        mb-2
      "
      />
      {items?.map((item, index) => (
        <div key={index}>
          {item}
          {index === 0 && <BannerUnderContentStrip items={bannerUnderItems} />}
          {index === 1 && (
            <div className="my-4 md:my-8">
              <DynamicHomepageFavorites fetchedPricesData={pricesData} />
            </div>
          )}
          {index !== 0 ? <div className="h-4 md:h-8" /> : null}
        </div>
      ))}

      {items && items.length > 0 && (
        <Link
          href={WebLink.PRODUCTS}
          aria-label="Tüm ürünleri görüntüle"
          className="text-base text-center content-center font-medium text-white bg-green-700 hover:bg-green-700 rounded-md w-fit mx-auto h-12 px-8 hover:scale-105 transition-all duration-300"
        >
          Tüm Ürünler
        </Link>
      )}

      <SeoView
        content={homePageResult?.homepage?.seo?.content || ''}
        className="mx-auto max-w-7xl"
        script={homePageResult?.homepage?.seo?.script}
      />
    </div>
  );
}
